import { useEffect, useState, useMemo, useCallback } from "react";
import { adminApi } from "../../api";

const TODAY = () => new Date().toISOString().slice(0, 10);

/**
 * Manages all state + side-effects for the commission payouts page:
 * - filters (minValue, tipoCliente, onlyAvailable, page)
 * - server fetch + payment types
 * - selection set + per-row overrides
 * - batch submit
 *
 * Returns a single object with values + setters. Designed to keep the
 * orchestrator page thin (presentation only).
 */
export default function usePayouts() {
  // server state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sumTotal, setSumTotal] = useState(0);
  const [targetCol, setTargetCol] = useState("DisponivelPagamento");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(200);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // filters
  const [minValue, setMinValue] = useState("0.01");
  const [tipoCliente, setTipoCliente] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true);

  // batch defaults
  const [data, setData] = useState(TODAY());
  const [tipoPag, setTipoPag] = useState("");
  const [obs, setObs] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);

  // selection + overrides
  const [selected, setSelected] = useState(new Set());
  const [overrides, setOverrides] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Load payment types once
  useEffect(() => {
    adminApi.opsPaymentTypes().then((r) => {
      const items = r?.data || [];
      setPaymentTypes(items);
      if (items.length) setTipoPag((cur) => cur || String(items[0].id));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await adminApi.opsCommissionsPayable({
        min_value: minValue || "0.01",
        tipo_cliente: tipoCliente || "",
        only_available: onlyAvailable,
        page,
        page_size: pageSize,
      });
      setRows(r.data || []);
      setTotal(r.total || 0);
      setSumTotal(r.sum_total || 0);
      setTargetCol(r.target_col || "DisponivelPagamento");
      // Drop selections for rows no longer in the list
      const visibleIds = new Set((r.data || []).map((x) => x.ID_Cliente));
      setSelected((prev) => new Set([...prev].filter((id) => visibleIds.has(id))));
    } catch (e) {
      setErr(e.message || "Erro a carregar comissões por pagar");
      setRows([]); setTotal(0); setSumTotal(0);
    } finally {
      setLoading(false);
    }
  }, [minValue, tipoCliente, onlyAvailable, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const valueOf = useCallback((r) => {
    const ov = overrides[r.ID_Cliente]?.valor;
    if (ov !== undefined) return Number(ov);
    return Number(r[targetCol] || 0);
  }, [overrides, targetCol]);

  const totalSelected = useMemo(() => {
    return rows
      .filter((r) => selected.has(r.ID_Cliente))
      .reduce((s, r) => s + (Number(valueOf(r)) || 0), 0);
  }, [rows, selected, valueOf]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => (prev.size === rows.length
      ? new Set()
      : new Set(rows.map((r) => r.ID_Cliente))));
  }, [rows]);

  const setOverride = useCallback((id, field, value) => {
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }, []);

  const submit = useCallback(async () => {
    if (selected.size === 0) { setErr("Escolhe pelo menos um consultor."); return; }
    if (!tipoPag) { setErr("Escolhe o tipo de pagamento."); return; }
    if (!data) { setErr("Data obrigatória."); return; }
    // Validate every selected row: valor must be > 0 AND ≤ DisponivelPagamento.
    // Backend faz a mesma validação, mas bloqueamos cedo para o utilizador
    // não ter de esperar pelo erro retornado pela API.
    const invalids = rows
      .filter((r) => selected.has(r.ID_Cliente))
      .map((r) => ({ r, v: Number(valueOf(r)), max: Number(r[targetCol] || 0) }))
      .filter(({ v, max }) => !(v > 0 && v <= max + 0.005));
    if (invalids.length > 0) {
      const first = invalids[0];
      setErr(
        `Valor inválido para ${first.r.NomeCompleto} (#${first.r.ID_Cliente}). ` +
        `Deve ser > 0 e ≤ ${first.max.toLocaleString("pt-PT", { style: "currency", currency: "EUR" })}.`
      );
      return;
    }
    const items = rows
      .filter((r) => selected.has(r.ID_Cliente))
      .map((r) => ({
        client_id: r.ID_Cliente,
        valor_pago: valueOf(r),
        observacoes: overrides[r.ID_Cliente]?.obs || undefined,
      }))
      .filter((it) => it.valor_pago && !Number.isNaN(it.valor_pago));

    if (items.length === 0) { setErr("Nenhum item com valor válido."); return; }

    const total = items.reduce((s, it) => s + it.valor_pago, 0);
    const totalFmt = total.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
    const confirmMsg = `Confirmar registo de ${items.length} pagamento(s), total ${totalFmt}?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true); setErr(""); setResult(null);
    try {
      const r = await adminApi.opsRegisterCommissionPaymentsBatch({
        data,
        id_tipo_pagamento: tipoPag,
        observacoes: obs || null,
        items,
      });
      setResult(r);
      setSelected(new Set());
      setOverrides({});
      await load();
    } catch (e) {
      setErr(e.message || "Erro no registo em lote");
    } finally {
      setSubmitting(false);
    }
  }, [selected, tipoPag, data, rows, valueOf, overrides, obs, load, targetCol]);

  return {
    // server data
    rows, total, sumTotal, targetCol, loading, err, page, pageSize, setPage,
    // filters
    minValue, setMinValue, tipoCliente, setTipoCliente, onlyAvailable, setOnlyAvailable,
    // batch
    data, setData, tipoPag, setTipoPag, obs, setObs, paymentTypes,
    // selection
    selected, overrides, valueOf, totalSelected,
    toggle, toggleAll, setOverride,
    // actions
    submit, submitting, result, load,
  };
}
